import React, { useEffect, useRef, useState } from 'react';
import { Card, Tooltip, Progress } from 'antd';
import { InfoCircleTwoTone, ArrowUpOutlined, ArrowDownOutlined, MinusOutlined } from '@ant-design/icons';
import { formatNumberWithCommas } from '../../../../utils/numbers';
import styles from './styles/data-card.module.scss';

const InfoIcon = () => (
    <Tooltip title="Additional information">
        <InfoCircleTwoTone twoToneColor="#8C8C8C" className={styles.infoIcon} />
    </Tooltip>
);

const DataCard = ({ title, value, percentage, showInfo = false, isCurrency = false, showProgress = false, index = 0 }) => {
    const valueRef = useRef(null);
    const containerRef = useRef(null);
    const [fontSize, setFontSize] = useState(36);

    const isPositive = percentage > 0;
    const isNegative = percentage < 0;
    const isZero = percentage === 0;

    let ArrowIcon, direction, changeClass;

    if (isPositive) {
        ArrowIcon = ArrowUpOutlined;
        direction = 'up';
        changeClass = styles.positive;
    } else if (isNegative) {
        ArrowIcon = ArrowDownOutlined;
        direction = 'down';
        changeClass = styles.negative;
    } else {
        ArrowIcon = MinusOutlined;
        direction = 'no change';
        changeClass = styles.neutral;
    }

    const animationDelay = `${0.4 + index * 0.05}s`;

    useEffect(() => {
        const adjustFontSize = () => {
            if (!valueRef.current || !containerRef.current) return;

            const valueElement = valueRef.current;
            const containerElement = containerRef.current;

            // Determine base font size based on screen size
            const isMobile = window.innerWidth < 768;
            const baseFontSize = isMobile ? 28 : 36;
            const minFontSize = 16;

            // Start with base font size
            let currentSize = baseFontSize;
            valueElement.style.fontSize = `${currentSize}px`;

            // Check if overflow occurs
            const checkOverflow = () => {
                const valueWidth = valueElement.scrollWidth;
                const containerWidth = containerElement.offsetWidth;
                const valueHeight = valueElement.scrollHeight;
                const containerHeight = containerElement.offsetHeight;

                return valueWidth > containerWidth || valueHeight > containerHeight;
            };

            // Reduce font size until it fits
            while (checkOverflow() && currentSize > minFontSize) {
                currentSize -= 1;
                valueElement.style.fontSize = `${currentSize}px`;
            }

            setFontSize(currentSize);
        };

        // Initial adjustment
        const timeout = setTimeout(adjustFontSize, 100);

        // Adjust on window resize
        window.addEventListener('resize', adjustFontSize);

        // Use ResizeObserver for container size changes
        // Use requestAnimationFrame to prevent ResizeObserver loop errors
        let rafId = null;
        const resizeObserver = new ResizeObserver(() => {
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
            rafId = requestAnimationFrame(() => {
                adjustFontSize();
                rafId = null;
            });
        });

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => {
            clearTimeout(timeout);
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
            window.removeEventListener('resize', adjustFontSize);
            resizeObserver.disconnect();
        };
    }, [value, isCurrency]);

    return (
        <Card className={styles.dataCard} title={title} extra={showInfo ? <InfoIcon /> : null} style={{ animationDelay }}>
            <div className={styles.dataContent} ref={containerRef}>
                <div className={styles.dataValue} ref={valueRef} style={{ fontSize: `${fontSize}px` }}>
                    {isCurrency ? `$${formatNumberWithCommas(value, true)}` : formatNumberWithCommas(value)}
                </div>
                {showProgress && <Progress percent={value} showInfo={false} strokeColor="#1890ff" className={styles.progressBar} />}
                {percentage !== null && percentage !== undefined && !showProgress && (
                    <div className={`${styles.dataChange} ${changeClass}`}>
                        <ArrowIcon className={styles.arrowIcon} />
                        {isZero ? (
                            'No change from last month'
                        ) : (
                            <>
                                <span className={styles.percentageNumber}>{Math.abs(percentage)}%</span>
                                <span className={styles.directionText}> {direction} from last month</span>
                            </>
                        )}
                    </div>
                )}
            </div>
        </Card>
    );
};

export default DataCard;
